import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { hasLevel, type Me, type Module, type PermissionLevel } from "@crc/shared";
import { api, ApiRequestError } from "./api.ts";

export type MeWithPrefs = Me & { preferences: Record<string, unknown>; assignedCompanyIds: string[] };

interface AuthValue {
  me: MeWithPrefs | null;
  loading: boolean;
  error: unknown;
  refresh: () => Promise<unknown>;
  signOut: () => Promise<void>;
  /** module-level permission check — mirrors the API's rbac() */
  can: (module: Module, level: PermissionLevel) => boolean;
  isStaff: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<MeWithPrefs>("/me");
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
  const me = q.data ?? null;
  const can = useCallback((module: Module, level: PermissionLevel) => hasLevel(me?.permissions[module] as PermissionLevel | undefined, level), [me]);
  const signOut = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    qc.clear();
    window.location.href = "/sign-in";
  }, [qc]);
  const value = useMemo<AuthValue>(
    () => ({ me, loading: q.isLoading, error: q.error, refresh: () => q.refetch(), signOut, can, isStaff: me?.userType === "internal", isAdmin: me?.role.key === "admin" }),
    [me, q, signOut, can],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** Home route per user type. */
export function homeFor(me: Me | null): string {
  if (!me) return "/sign-in";
  switch (me.userType) {
    case "mashgiach":
      return "/app";
    case "company":
      return "/";
    case "location_user":
      return `/companies/${me.companyId ?? ""}`;
    default:
      return "/";
  }
}
